import { Card, Row, Col, Table, Badge } from "react-bootstrap"
import { Building, DollarSign, CreditCard, Users, Package, Activity, Calendar, Lock, FileText } from "lucide-react"

export default function NightAuditSummary({ report }) {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount)
  }

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const getStatusBadge = (status) => {
    const variant = status === "active" ? "success" : "secondary"
    return <Badge bg={variant}>{status}</Badge>
  }

  if (!report) {
    return (
      <div className="text-center py-5 text-muted">
        <p>No report data available.</p>
      </div>
    )
  }

  return (
    <div className="container-fluid p-4">
      {/* Header */}
      <div className="text-center mb-5">
        <h1 className="display-4 fw-bold text-dark">Hotel Daily Report</h1>
        <p className="text-muted">Comprehensive daily operations summary</p>
      </div>

      {/* Room & Reservation Summary */}
      <Card className="mb-4">
        <Card.Header>
          <Card.Title className="d-flex align-items-center gap-2">
            <Building size={20} />
            Room & Reservation Summary
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <Row className="g-3 mb-4">
            <Col xs={6} md={3}>
              <div className="text-center p-3 bg-primary bg-opacity-10 rounded">
                <div className="fs-2 fw-bold text-white">{report.roomSummary.totalRooms}</div>
                <div className="small text-muted">Total Rooms</div>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="text-center p-3 bg-success bg-opacity-10 rounded">
                <div className="fs-2 fw-bold text-success">{report.roomSummary.occupiedRooms}</div>
                <div className="small text-muted">Occupied</div>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="text-center p-3 bg-secondary bg-opacity-10 rounded">
                <div className="fs-2 fw-bold text-secondary">{report.roomSummary.availableRooms}</div>
                <div className="small text-muted">Available</div>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="text-center p-3 bg-info bg-opacity-10 rounded">
                <div className="fs-2 fw-bold text-info">{report.roomSummary.inHouseGuests}</div>
                <div className="small text-muted">In-House Guests</div>
              </div>
            </Col>
          </Row>
          <hr />
          <Row className="g-3">
            <Col xs={6} md={3}>
              <div className="d-flex justify-content-between">
                <span>Check-ins:</span>
                <span className="fw-medium">{report.roomSummary.checkIns}</span>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="d-flex justify-content-between">
                <span>Check-outs:</span>
                <span className="fw-medium">{report.roomSummary.checkOuts}</span>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="d-flex justify-content-between">
                <span>No Shows:</span>
                <span className="fw-medium">{report.roomSummary.noShows}</span>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="d-flex justify-content-between">
                <span>Cancellations:</span>
                <span className="fw-medium">{report.roomSummary.cancellations}</span>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Revenue Summary */}
      <Card className="mb-4">
        <Card.Header>
          <Card.Title className="d-flex align-items-center gap-2">
            <DollarSign size={20} />
            Revenue Summary
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <Row className="g-4 mb-4">
            <Col md={4}>
              <div className="text-center p-4 bg-success bg-opacity-10 rounded">
                <div className="fs-3 fw-bold text-success">{formatCurrency(report.revenueSummary.totalRevenue)}</div>
                <div className="small text-muted">Total Revenue</div>
              </div>
            </Col>
            <Col md={4}>
              <div className="text-center p-4 bg-primary bg-opacity-10 rounded">
                <div className="fs-3 fw-bold text-white">{formatCurrency(report.revenueSummary.roomRevenue)}</div>
                <div className="small text-muted">Room Revenue</div>
              </div>
            </Col>
            <Col md={4}>
              <div className="text-center p-4 bg-warning bg-opacity-10 rounded">
                <div className="fs-3 fw-bold text-warning">{formatCurrency(report.revenueSummary.fnbRevenue)}</div>
                <div className="small text-muted">F&B Revenue</div>
              </div>
            </Col>
          </Row>
          <div className="row g-2">
            {/* <div className="col-md-4">
              <div className="d-flex justify-content-between">
                <span>Other Charges:</span>
                <span className="fw-medium">{formatCurrency(report.revenueSummary.otherCharges)}</span>
              </div>
            </div>
            <div className="col-md-4">
              <div className="d-flex justify-content-between">
                <span>Total Taxes:</span>
                <span className="fw-medium">{formatCurrency(report.revenueSummary.totalTaxes)}</span>
              </div>
            </div> */}
            <div className="col-md-4">
              <div className="d-flex justify-content-between text-danger">
                <span>Total Discounts:</span>
                <span className="fw-medium">-{formatCurrency(report.revenueSummary.totalDiscounts)}</span>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Payment Summary */}
      <Card className="mb-4">
        <Card.Header>
          <Card.Title className="d-flex align-items-center gap-2">
            <CreditCard size={20} />
            Payment Summary
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <h6 className="fw-medium text-muted mb-3">Payment Methods</h6>
          <Row className="g-2 mb-4">
            {Object.entries(report.paymentSummary.paymentsByMethod).map(([method, amount]) => (
              <Col key={method} xs={6} md={4}>
                <div className="d-flex justify-content-between p-2 bg-light rounded">
                  <span className="text-capitalize">{method.replace("_", " ")}:</span>
                  <span className="fw-medium">{formatCurrency(amount)}</span>
                </div>
              </Col>
            ))}
          </Row>
          {/* <hr />
          <Row className="g-2">
            <Col md={4}>
              <div className="d-flex justify-content-between">
                <span>Deposits Collected:</span>
                <span className="fw-medium">{formatCurrency(report.paymentSummary.depositsCollected)}</span>
              </div>
            </Col>
            <Col md={4}>
              <div className="d-flex justify-content-between">
                <span>Deposits Refunded:</span>
                <span className="fw-medium">{formatCurrency(report.paymentSummary.depositsRefunded)}</span>
              </div>
            </Col>
            <Col md={4}>
              <div className="d-flex justify-content-between">
                <span>Pending Payments:</span>
                <span className="fw-medium">{formatCurrency(report.paymentSummary.pendingPayments)}</span>
              </div>
            </Col>
          </Row> */}
        </Card.Body>
      </Card>

      {/* Guest Ledger */}
      <Card className="mb-4">
        <Card.Header>
          <Card.Title className="d-flex align-items-center gap-2">
            <Users size={20} />
            Guest Ledger
          </Card.Title>
        </Card.Header>
        <Card.Body>
          {report.guestLedger?.guestLedger?.length > 0 ? (
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Guest Name</th>
                    <th>Folio Number</th>
                    <th>Status</th>
                    <th className="text-end">Charges</th>
                    <th className="text-end">Payments</th>
                    <th className="text-end">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {report.guestLedger.guestLedger.map((guest) => (
                    <tr key={guest.guestId}>
                      <td className="fw-medium">{guest.guestName}</td>
                      <td>{guest.folioNumber}</td>
                      <td>{getStatusBadge(guest.folioStatus)}</td>
                      <td className="text-end">{formatCurrency(guest.totalCharges)}</td>
                      <td className="text-end">{formatCurrency(guest.totalPayments)}</td>
                      <td className="text-end">
                        <span className={guest.balance > 0 ? "text-danger" : "text-success"}>
                          {formatCurrency(guest.balance)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-5 text-muted">No guest ledger entries found.</div>
          )}
        </Card.Body>
      </Card>

      {/* Inventory Usage */}
      <Card className="mb-4">
        <Card.Header>
          <Card.Title className="d-flex align-items-center gap-2">
            <Package size={20} />
            Inventory Usage
          </Card.Title>
        </Card.Header>
        <Card.Body>
          {report.inventoryUsage?.usageSummary?.length > 0 ? (
            <div className="row g-3">
              {report.inventoryUsage.usageSummary.map((item, index) => (
                <Col key={index} md={6}>
                  <div className="p-3 border rounded">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div>
                        <h6 className="fw-medium mb-1">{item.itemName}</h6>
                        <small className="text-muted">{item.department}</small>
                      </div>
                      <div className="text-end">
                        <span className="fs-5 fw-bold text-danger">-{item.changeAmount}</span>
                      </div>
                    </div>
                    <p className="small text-muted mb-0">{item.notes}</p>
                  </div>
                </Col>
              ))}
            </div>
          ) : (
            <div className="text-center py-5 text-muted">No inventory usage entries found.</div>
          )}
        </Card.Body>
      </Card>

      {/* Front Office Activity Log */}
      <Card className="mb-4">
        <Card.Header>
          <Card.Title className="d-flex align-items-center gap-2">
            <Activity size={20} />
            Front Office Activity Log
          </Card.Title>
        </Card.Header>
        <Card.Body>
          {report.activityLog?.staffActivity?.length > 0 ? (
            <div className="row g-3">
              {report.activityLog.staffActivity.map((activity, index) => (
                <Col key={index} xs={12}>
                  <div className="p-3 border-start border-primary border-4 bg-primary bg-opacity-10 rounded-end">
                    <div className="d-flex justify-content-between align-items-start mb-1">
                      <h6 className="fw-medium text-capitalize mb-0">{activity.action}</h6>
                      <small className="text-muted">{formatDateTime(activity.timestamp)}</small>
                    </div>
                    <p className="small text-muted mb-1">{activity.remarks}</p>
                    <div className="small text-muted">
                      By: {activity.staffName} ({activity.staffRole})
                    </div>
                  </div>
                </Col>
              ))}
            </div>
          ) : (
            <div className="text-center py-5 text-muted">No activity log entries found.</div>
          )}
        </Card.Body>
      </Card>

      <Row className="g-4 mb-4">
        {/* Date Rollover */}
        <Col md={6}>
          <Card>
            <Card.Header>
              <Card.Title className="d-flex align-items-center gap-2">
                <Calendar size={20} />
                Date Rollover
              </Card.Title>
            </Card.Header>
            <Card.Body>
              <div className="row g-2">
                {report.dateRollover?.actions?.map((action, index) => (
                  <Col key={index} xs={12}>
                    <div className="d-flex align-items-center gap-2 p-2 bg-light rounded">
                      <div className="bg-success rounded-circle" style={{ width: "8px", height: "8px" }}></div>
                      <span className="small">{action}</span>
                    </div>
                  </Col>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* System Locking */}
        <Col md={6}>
          <Card>
            <Card.Header>
              <Card.Title className="d-flex align-items-center gap-2">
                <Lock size={20} />
                System Locking
              </Card.Title>
            </Card.Header>
            <Card.Body>
              <div className="row g-2">
                {report.systemLocking?.actions?.map((action, index) => (
                  <Col key={index} xs={12}>
                    <div className="d-flex align-items-center gap-2 p-2 bg-light rounded">
                      <div className="bg-success rounded-circle" style={{ width: "8px", height: "8px" }}></div>
                      <span className="small">{action}</span>
                    </div>
                  </Col>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Generated Reports */}
      <Card className="mb-4">
        <Card.Header>
          <Card.Title className="d-flex align-items-center gap-2">
            <FileText size={20} />
            Generated Reports
          </Card.Title>
          <Card.Subtitle className="text-muted">
            Generated at: {formatDateTime(report.generatedReports.generatedAt)}
          </Card.Subtitle>
        </Card.Header>
        <Card.Body>
          <Row className="g-2">
            {report.generatedReports.generatedReports.map((reportName, index) => (
              <Col key={index} md={6}>
                <div className="d-flex align-items-center gap-2 p-2 bg-success bg-opacity-10 rounded">
                  <FileText size={16} className="text-success" />
                  <span className="small">{reportName}</span>
                </div>
              </Col>
            ))}
          </Row>
        </Card.Body>
      </Card>
    </div>
  )
}
